
import { supabase } from "@/integrations/supabase/client";

/**
 * Require MFA for a user
 */
export async function requireMFA(userId: string): Promise<boolean> {
  try {
    console.log("Setting up MFA requirement for user:", userId);
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    
    if (!token) {
      console.error("No active session found for MFA setup");
      return false;
    }
    
    try {
      // First check if MFA is already enabled via the edge function
      console.log("Checking existing MFA status");
      const response = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-2fa-settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const responseData = await response.json();
      console.log("MFA status check response:", responseData);
      
      if (!response.ok) {
        console.error(`Error checking MFA status: ${response.statusText}`);
        
        // If there was an error but it's because the table doesn't exist yet, treat it as successful
        if (responseData.warning && responseData.warning.includes("table_not_ready")) {
          console.log("MFA table not ready yet, but continuing");
          return true;
        }
        
        // If the direct database approach is needed, use the edge function to create the record
        console.log("Attempting to create MFA record");
        const createMfaResponse = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-2fa-settings`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            requires_2fa: true,
            has_completed_2fa: false
          })
        });
        
        if (!createMfaResponse.ok) {
          console.error(`Error creating MFA record: ${createMfaResponse.statusText}`);
          return false;
        }
        
        const createResponseData = await createMfaResponse.json();
        console.log("MFA creation response:", createResponseData);
        return createResponseData.success === true;
      }
      
      const mfaData = responseData.data;
      console.log("Current MFA data:", mfaData);
      
      // If MFA is not yet required for this user, enable it
      if (!mfaData || !mfaData.requires_2fa) {
        console.log("Enabling MFA for user");
        const updateResponse = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-2fa-settings`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            requires_2fa: true,
            has_completed_2fa: false
          })
        });
        
        if (!updateResponse.ok) {
          console.error(`Error enabling MFA: ${updateResponse.statusText}`);
          return false;
        }
        
        const updateResponseData = await updateResponse.json();
        console.log("MFA update response:", updateResponseData);
        return updateResponseData.success === true;
      }
      
      // MFA is already set up
      console.log("MFA is already properly configured");
      return true;
    } catch (error: any) {
      console.error("Error during MFA setup:", error);
      return false;
    }
  } catch (error: any) {
    console.error("Error requiring MFA:", error);
    return false;
  }
}

/**
 * Get MFA status for the current user
 */
export async function getMFAStatus(): Promise<{
  exists: boolean;
  requires2FA: boolean;
  completed2FA: boolean;
  errorDetails?: string;
}> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    
    if (!token) {
      return {
        exists: false,
        requires2FA: false,
        completed2FA: false,
        errorDetails: "No active session"
      };
    }
    
    try {
      console.log("Checking MFA status with token");
      const response = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-2fa-settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error("Error response from MFA check:", errorData);
        return {
          exists: false,
          requires2FA: false,
          completed2FA: false,
          errorDetails: `API error: ${response.status} ${response.statusText}`
        };
      }
      
      const mfaData = await response.json();
      console.log("MFA data response:", mfaData);
      
      if (mfaData.warning && mfaData.warning.includes("table_not_ready")) {
        return {
          exists: false,
          requires2FA: false,
          completed2FA: false,
          errorDetails: "MFA table not set up yet"
        };
      }
      
      if (!mfaData.data) {
        return {
          exists: false,
          requires2FA: false,
          completed2FA: false
        };
      }
      
      return {
        exists: true,
        requires2FA: !!mfaData.data.requires_2fa,
        completed2FA: !!mfaData.data.has_completed_2fa
      };
    } catch (error: any) {
      console.error("Network error checking MFA status:", error);
      return {
        exists: false,
        requires2FA: false,
        completed2FA: false,
        errorDetails: `Network error: ${error.message}`
      };
    }
  } catch (error: any) {
    console.error("Error checking MFA status:", error);
    return {
      exists: false,
      requires2FA: false,
      completed2FA: false,
      errorDetails: error.message
    };
  }
}

/**
 * Updates the MFA settings for a user
 */
export async function updateMFASettings(settings: { 
  requires_2fa?: boolean;
  has_completed_2fa?: boolean; 
}): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    
    if (!token) {
      return {
        success: false,
        message: "No active session found"
      };
    }
    
    const response = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-2fa-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings)
    });
    
    const responseData = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: responseData.error || "Failed to update MFA settings"
      };
    }
    
    return {
      success: true,
      message: "MFA settings updated successfully"
    };
  } catch (error: any) {
    console.error("Error updating MFA settings:", error);
    return {
      success: false,
      message: error.message || "Unknown error occurred"
    };
  }
}
